import { ukDate } from "../lib/dates";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, mediaUrl } from "../lib/api";
import DocViewer from "../components/DocViewer";
import { toast } from "sonner";
import { ArrowLeft, Check, Circle, FileText, BookOpen, PlayCircle, Award, Upload, ClipboardList, MessageSquare, UserPlus, Users, Send, MessagesSquare, ShieldCheck, GraduationCap, Pin, Video, Info, Target, Eye, ClipboardCheck, Inbox, GripVertical, Download, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import RepConsole from "./RepConsole";
import { MarkdownLite, courseFiles } from "../components/RichText";
import { CourseGuide, CourseGuideBanner } from "../components/CourseGuide";
import { SubmissionCelebration } from "../components/SubmissionCelebration";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

function embedUrl(url = "") {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

export default function PortalCourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [done, setDone] = useState([]);
  const [active, setActive] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [subText, setSubText] = useState("");
  const [subFiles, setSubFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [repOpen, setRepOpen] = useState(false);
  const [repForm, setRepForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [repSaving, setRepSaving] = useState(false);
  const [cohortInfo, setCohortInfo] = useState(null);
  const [tab, setTab] = useState("course");
  const [posts, setPosts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [discMsg, setDiscMsg] = useState("");
  const [discSending, setDiscSending] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [celebrate, setCelebrate] = useState("");
  const [annReply, setAnnReply] = useState({});
  const [annReplyBusy, setAnnReplyBusy] = useState("");
  const fileRef = useRef();
  const seenFired = useRef(new Set());

  const downloadFeedbackPdf = async (subId) => {
    try {
      const res = await api.get(`/portal/submissions/${subId}/feedback-summary.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) { toast.error("Could not open the feedback summary"); }
  };

  const sendAnnReply = async (subId, annId) => {
    const txt = (annReply[annId] || "").trim();
    if (!txt) return;
    setAnnReplyBusy(annId);
    try {
      await api.post(`/assignment-submissions/${subId}/annotations/${annId}/reply`, { text: txt });
      setAnnReply((d) => ({ ...d, [annId]: "" }));
      toast.success("Reply sent to your tutor");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send reply"); }
    finally { setAnnReplyBusy(""); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/courses/${id}`);
      setCourse(data); setDone(data.completed_lesson_ids || []);
      setActive((prev) => prev || (data.lessons?.[0]?.id ?? null));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/my-classes"); }
  };
  const loadCohort = async () => {
    try {
      const { data } = await api.get(`/portal/courses/${id}/cohort`);
      setCohortInfo(data);
      const { data: p } = await api.get(`/portal/courses/${id}/cohort/discussion`);
      setPosts(p);
      const { data: ss } = await api.get(`/portal/courses/${id}/sessions`);
      // Only surface the immediate next live class (not every upcoming one, and never past ones)
      const withLink = (ss.upcoming || []).filter((s) => (s.meeting_link || "").trim());
      setSessions(withLink.slice(0, 1));
    } catch (err) { /* not enrolled / no cohort context */ }
  };
  useEffect(() => { load(); loadCohort(); }, [id]); // eslint-disable-line
  useEffect(() => {
    if (!course) return;
    Object.values(course.my_submissions || {}).forEach((s) => {
      if (s.unseen_tutor_replies > 0 && !seenFired.current.has(s.id)) {
        seenFired.current.add(s.id);
        api.post(`/portal/submissions/${s.id}/replies-seen`).catch(() => {});
      }
    });
  }, [course]); // eslint-disable-line
  const joinSession = async (s) => {
    if (s.meeting_link) window.open(s.meeting_link, "_blank", "noopener");
    try {
      const { data } = await api.post(`/portal/courses/${id}/sessions/${s.id}/join`);
      if (data.attendance_marked) toast.success("You're marked present — enjoy the class! 🎉");
      loadCohort();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const markSeen = async (p) => {
    if (!p.pinned) return;
    try { await api.post(`/portal/courses/${id}/cohort/discussion/${p.id}/seen`); } catch (e) { /* ignore */ }
  };
  const sendDisc = async () => {
    if (!discMsg.trim()) return;
    setDiscSending(true);
    try {
      await api.post(`/portal/courses/${id}/cohort/discussion`, { text: discMsg });
      setDiscMsg("");
      const { data: p } = await api.get(`/portal/courses/${id}/cohort/discussion`);
      setPosts(p);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDiscSending(false); }
  };

  // reset submission form when switching lessons
  useEffect(() => { setSubText(""); setSubFiles([]); if (fileRef.current) fileRef.current.value = ""; }, [active]);

  const toggle = async (l) => {
    const isDone = done.includes(l.id);
    try {
      if (isDone) { await api.post(`/portal/lessons/${l.id}/uncomplete`); setDone((d) => d.filter((x) => x !== l.id)); }
      else { await api.post(`/portal/lessons/${l.id}/complete`); setDone((d) => [...d, l.id]); toast.success("Lesson completed! 🎉"); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [dragFrom, setDragFrom] = useState(null);
  const subPreviews = useMemo(() => subFiles.map((f) => (f.type || "").startsWith("image/") ? URL.createObjectURL(f) : null), [subFiles]);
  useEffect(() => () => subPreviews.forEach((u) => u && URL.revokeObjectURL(u)), [subPreviews]);
  const reorderFiles = (from, to) => {
    if (from == null || to == null || from === to) return;
    setSubFiles((prev) => { const a = [...prev]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; });
  };
  const submitAssignment = async (lesson) => {
    if (!subText.trim() && subFiles.length === 0) { toast.error("Add a written response or attach a file"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("text", subText);
      subFiles.forEach((f) => fd.append("files", f));
      const { data: res } = await api.post(`/portal/lessons/${lesson.id}/assignment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res?.celebrate) setCelebrate(lesson.title || "your assignment");
      else toast.success("Assignment submitted 🎉");
      setSubText(""); setSubFiles([]); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const addLearnerRep = async () => {
    if (!repForm.first_name.trim()) { toast.error("Enter the learner's first name"); return; }
    setRepSaving(true);
    try {
      await api.post(`/portal/courses/${id}/add-learner`, repForm);
      toast.success("Learner added to the class 🎉");
      setRepOpen(false); setRepForm({ first_name: "", last_name: "", email: "", phone: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRepSaving(false); }
  };

  const downloadSub = async (sub) => {    try {
      const res = await api.get(`/assignment-submissions/${sub.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) { toast.error("Could not open file"); }
  };

  const downloadCertificate = async () => {    setDownloading(true);
    try {
      const res = await api.get(`/portal/courses/${id}/certificate`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(course?.title || "Course").replace(/[^\w -]/g, "").replace(/\s+/g, "_")}_Certificate.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Certificate downloaded 🎓");
    } catch (err) {
      let msg = "Could not generate certificate";
      try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch {}
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  if (!course) return <div className="p-8" style={{ color: "var(--sj-text-3)" }}>Loading...</div>;
  const lessons = course.lessons || [];
  const activeLesson = lessons.find((l) => l.id === active) || lessons[0];
  const pct = lessons.length ? Math.round((done.length / lessons.length) * 100) : 0;
  const subs = course.my_submissions || {};
  const status = course.course_status || {};
  const mySub = activeLesson ? subs[activeLesson.id] : null;
  const needsAssignment = activeLesson?.assignment_required && !mySub;
  const tabs = [
    { id: "course", label: "Course", icon: BookOpen },
    ...(course.is_class_rep ? [{ id: "attendance", label: "Attendance", icon: ClipboardCheck }, { id: "submissions", label: "Assignment Submission", icon: Inbox }] : []),
    ...(cohortInfo ? [{ id: "cohort", label: "My Cohort", icon: GraduationCap }, { id: "discussion", label: "Class Discussion", icon: MessagesSquare }] : []),
    { id: "guide", label: "How it works", icon: Info },
  ];
  const activeTab = tabs.some((t) => t.id === tab) ? tab : "course";

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="portal-course-detail">
      <button data-testid="back-to-my-classes" onClick={() => navigate("/my-classes")} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>
        <ArrowLeft className="h-4 w-4" /> Back to My Courses
      </button>

      {(() => {
        const resubmitNeeded = lessons.filter((l) => subs[l.id]?.resubmission_allowed);
        if (resubmitNeeded.length === 0) return null;
        return (
          <div data-testid="resubmit-action-banner" className="rounded-2xl border p-4 sm:p-5" style={{ borderColor: "#E9CBA3", background: "#FFF7EC" }}>
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#F6D9A8" }}><AlertTriangle className="h-5 w-5" style={{ color: "#8a5a1a" }} /></div>
              <div className="min-w-0 flex-1">
                <p className="font-head text-lg font-semibold" style={{ color: "#8a5a1a" }}>Action needed: resubmit {resubmitNeeded.length === 1 ? "an assignment" : `${resubmitNeeded.length} assignments`}</p>
                <p className="text-sm mt-0.5" style={{ color: "#8a5a1a" }}>Your tutor has asked you to submit again. Open the assignment below to replace your work.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {resubmitNeeded.map((l) => (
                    <button key={l.id} data-testid={`resubmit-jump-${l.id}`} onClick={() => { setTab("course"); setActive(l.id); }}
                      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold text-white hover:brightness-105" style={{ background: "#8a5a1a" }}>
                      <Upload className="h-3.5 w-3.5" /> {l.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className={`${CARD} p-6`}>
        {course.cover_image && <img src={mediaUrl(course.cover_image.file_id)} alt="" data-testid="course-cover-image" className="-mx-6 -mt-6 mb-4 h-44 w-[calc(100%+3rem)] object-cover rounded-t-2xl" />}
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--sj-text-3)" }}><BookOpen className="h-4 w-4" /> Course</div>
        <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight">{course.title}</h1>
        {course.description && <p className="mt-1" style={{ color: "var(--sj-text-2)" }}>{course.description}</p>}
        {course.finished && <div data-testid="course-finished-banner" className="mt-3 rounded-xl px-4 py-2.5 text-sm" style={{ background: "#EDF0FF", color: "#3B5BDB" }}>This course has finished. You can still view the lessons, your feedback and your certificate, but new work can't be submitted.</div>}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: "var(--sj-border)" }}>
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--sj-secondary)" }} />
          </div>
          <span className="text-sm font-semibold" style={{ color: "var(--sj-text-2)" }}>{pct}% complete</span>
        </div>
        {course.progress?.breakdown && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="progress-breakdown">
            <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: "var(--sj-text-3)" }}>
              Made up of:
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" data-testid="progress-breakdown-info" aria-label="How progress is worked out" className="inline-flex"><Info className="h-3.5 w-3.5" style={{ color: "var(--sj-text-3)" }} /></button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[240px] text-xs" data-testid="progress-breakdown-tip">
                    Your progress is worked out from the classes you've attended and the assignments your tutor has marked — there's no button to press.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" data-testid="progress-attendance" style={{ background: "#E3F1EC", color: "var(--sj-primary)" }}>
              <ClipboardCheck className="h-3.5 w-3.5" /> Attendance {course.progress.breakdown.attendance.attended}/{course.progress.breakdown.attendance.total}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" data-testid="progress-assignments" style={{ background: "#FFF7EC", color: "#8a5a1a" }}>
              <ClipboardList className="h-3.5 w-3.5" /> Marked assignments {course.progress.breakdown.assignments.completed}/{course.progress.breakdown.assignments.total}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="course-tabs">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`course-tab-${t.id}`} onClick={() => setTab(t.id)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-all"
            style={activeTab === t.id ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === "guide" && <CourseGuide />}
      {activeTab === "course" && (<div className="space-y-6">
      <CourseGuideBanner courseId={id} onOpen={() => setTab("guide")} />
      {(course.information?.trim() || course.learning_goals?.trim() || courseFiles(course).length > 0) && (
        <div className={`${CARD} p-6`} data-testid="course-about-card">
          {course.information?.trim() && (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-sm font-semibold mb-1" style={{ color: "var(--sj-primary)" }}><Info className="h-4 w-4" /> About this course</div>
              <MarkdownLite text={course.information} className="text-sm" style={{ color: "var(--sj-text-2)" }} />
            </div>
          )}
          {course.learning_goals?.trim() && (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-sm font-semibold mb-1" style={{ color: "var(--sj-primary)" }}><Target className="h-4 w-4" /> Learning goals</div>
              <ul className="text-sm space-y-1" style={{ color: "var(--sj-text-2)" }}>
                {course.learning_goals.split("\n").filter((l) => l.trim()).map((l, i) => (
                  <li key={i} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--sj-secondary)" }} /><span>{l.replace(/^[-*•]\s*/, "")}</span></li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-1.5">
            {courseFiles(course).map((f, i) => (
              <button key={i} data-testid={`about-info-file-btn-${i}`} onClick={() => setViewer({ path: `/media/${f.file_id}`, fileName: f.name, contentType: f.content_type })} className="w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm hover:bg-[#F1F8F5]" style={{ borderColor: "var(--sj-border)" }}>
                <span className="flex items-center gap-2 truncate" style={{ color: "var(--sj-text-2)" }}>
                  {courseFiles(course).length > 1 && <span className="flex items-center justify-center h-5 w-5 shrink-0 rounded-full text-[11px] font-bold text-white" style={{ background: "var(--sj-primary)" }}>{i + 1}</span>}
                  <FileText className="h-4 w-4 shrink-0" style={{ color: "var(--sj-primary)" }} /> {f.name}
                </span>
                <span className="inline-flex items-center gap-1 shrink-0 font-semibold" style={{ color: "var(--sj-primary)" }}><Eye className="h-4 w-4" /> View</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {status.certificate_issued ? (
        <div data-testid="certificate-banner" className="rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4" style={{ background: "linear-gradient(100deg, rgba(11,46,41,0.12), rgba(11,94,78,0.12))", border: "1px solid var(--sj-border)" }}>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--sj-secondary)" }}><Award className="h-7 w-7 text-white" /></div>
            <div>
              <div className="font-head text-lg font-semibold">Certificate approved — congratulations! 🎓</div>
              <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Your tutor has issued your Certificate of Completion.</div>
            </div>
          </div>
          <button data-testid="download-certificate-button" onClick={downloadCertificate} disabled={downloading}
            className="flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
            <Award className="h-5 w-5" /> {downloading ? "Preparing..." : "Download Certificate"}
          </button>
        </div>
      ) : status.completed ? (
        <div data-testid="completed-awaiting-cert" className="rounded-2xl p-5 flex items-center gap-3" style={{ background: "#EAF6F1", border: "1px solid var(--sj-border)" }}>
          <Check className="h-5 w-5 shrink-0" style={{ color: "var(--sj-secondary)" }} />
          <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Your tutor has marked this course as <b>completed</b>. Your certificate will unlock once it has been approved.</div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`${CARD} p-3 md:col-span-1 h-fit`} data-testid="lesson-nav">
          <div className="text-xs font-semibold uppercase tracking-wider px-3 py-2" style={{ color: "var(--sj-text-3)" }}>Lessons</div>
          {lessons.length === 0 && <div className="px-3 py-6 text-sm text-center" style={{ color: "var(--sj-text-3)" }}>No lessons yet.</div>}
          {lessons.map((l, i) => {
            const isDone = done.includes(l.id);
            return (
              <button key={l.id} data-testid={`lesson-nav-${l.id}`} onClick={() => setActive(l.id)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-colors"
                style={{ background: active === l.id ? "var(--sj-peach)" : "transparent" }}>
                {isDone ? <Check className="h-4 w-4 shrink-0" style={{ color: "var(--sj-secondary)" }} /> : <Circle className="h-4 w-4 shrink-0" style={{ color: "var(--sj-text-3)" }} />}
                <span className="text-sm font-medium truncate flex-1" style={{ color: active === l.id ? "var(--sj-primary)" : "var(--sj-text)" }}>{i + 1}. {l.title}</span>
                {l.assignment && <ClipboardList className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--sj-text-3)" }} />}
              </button>
            );
          })}
        </div>

        <div className="md:col-span-2 space-y-4">
          {activeLesson ? (
            <div className={`${CARD} p-6`} data-testid="active-lesson">
              <h2 className="font-head text-2xl font-semibold">{activeLesson.title}</h2>
              {activeLesson.meeting_link && (
                <a data-testid="join-meeting-link" href={activeLesson.meeting_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
                  <Video className="h-4 w-4" /> Join live class
                </a>
              )}
              {activeLesson.video_url && (
                <div className="mt-4 aspect-video rounded-xl overflow-hidden bg-black">
                  <iframe title="lesson-video" src={embedUrl(activeLesson.video_url)} className="w-full h-full" allowFullScreen frameBorder="0" />
                </div>
              )}
              {activeLesson.content && <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed" style={{ color: "var(--sj-text)" }}>{activeLesson.content}</p>}
              {activeLesson.file_id && (
                <button data-testid="lesson-file-link" onClick={() => setViewer({ path: `/media/${activeLesson.file_id}`, fileName: activeLesson.file_name || "material", contentType: "" })} className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                  <FileText className="h-4 w-4" /> {activeLesson.file_name || "Open material"}
                </button>
              )}
              {activeLesson.doc_url && (
                <a href={activeLesson.doc_url} target="_blank" rel="noreferrer" className="mt-4 ml-2 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                  <FileText className="h-4 w-4" /> Open document
                </a>
              )}

              {activeLesson.assignment && (
                <div className="mt-6 rounded-xl p-4" data-testid="assignment-block" style={{ background: "#F6FAF9", border: "1px solid var(--sj-border)" }}>
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--sj-primary)" }}>
                    <ClipboardList className="h-4 w-4" /> Assignment {activeLesson.assignment_required && <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "var(--sj-secondary)", color: "#fff" }}>Required</span>}
                    {activeLesson.assignment_due_date && <span data-testid="assignment-due-date" className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FFF7EC", color: "#8a5a1a" }}>Due {ukDate(activeLesson.assignment_due_date)}</span>}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm" style={{ color: "var(--sj-text)" }}>{activeLesson.assignment}</p>

                  {mySub ? (
                    <div className="mt-4 rounded-lg p-3" data-testid="my-submission" style={{ background: "#fff", border: "1px solid var(--sj-border)" }}>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Check className="h-4 w-4" style={{ color: "var(--sj-secondary)" }} />
                        Submitted{mySub.status === "reviewed" ? " · Reviewed by tutor" : " · Awaiting review"}
                      </div>
                      {mySub.text && <p className="mt-1.5 text-sm whitespace-pre-wrap" style={{ color: "var(--sj-text-2)" }}>{mySub.text}</p>}
                      {(() => {
                        const fList = (mySub.files && mySub.files.length) ? mySub.files : (mySub.file_name ? [{ file_name: mySub.file_name, content_type: mySub.content_type }] : []);
                        return fList.length > 0 && (
                          <div className="mt-2 space-y-1" data-testid="my-submission-files">
                            {fList.length > 1 && <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>{fList.length} documents</div>}
                            {fList.map((f, i) => (
                              <button key={i} data-testid={`my-submission-file-${i}`} onClick={() => setViewer({ path: `/assignment-submissions/${mySub.id}/download?idx=${i}`, fileName: f.file_name, contentType: f.content_type || "", subId: mySub.id, annotations: mySub.pdf_annotations || [], fileIdx: i })} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--sj-primary)" }}>
                                <FileText className="h-4 w-4 shrink-0" /> {f.file_name}
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                      {mySub.feedback && (
                        <div className="mt-2 rounded-lg p-2.5 flex gap-2" style={{ background: "#F6FAF9" }}>
                          <MessageSquare className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--sj-primary)" }} />
                          <div className="text-sm" style={{ color: "var(--sj-text)" }}><b>Tutor feedback:</b> {mySub.feedback}</div>
                        </div>
                      )}
                      {(mySub.pdf_annotations || []).filter((a) => (a.text || "").trim()).length > 0 && (
                        <div className="mt-2 rounded-lg p-2.5" style={{ background: "#FFF7EC" }} data-testid="annotation-summary">
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "#8a5a1a" }}><MessageSquare className="h-3.5 w-3.5" /> Tutor comments on your pages</div>
                            <button data-testid="download-feedback-pdf" onClick={() => downloadFeedbackPdf(mySub.id)} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap hover:bg-white" style={{ borderColor: "#E7CFA0", color: "#8a5a1a", background: "#FFFDF8" }}>
                              <Download className="h-3.5 w-3.5" /> Summary PDF
                            </button>
                          </div>
                          <div className="space-y-2.5">
                            {(mySub.pdf_annotations || []).filter((a) => (a.text || "").trim())
                              .slice().sort((a, b) => ((a.file_idx || 0) - (b.file_idx || 0)) || ((a.page || 1) - (b.page || 1)))
                              .map((a, i) => (
                                <div key={a.id || i} data-testid={`annotation-item-${i}`} className="rounded-lg p-2" style={{ background: "#fff", border: "1px solid #F0E4CE" }}>
                                  <div className="text-sm" style={{ color: "var(--sj-text)" }}>
                                    <span className="text-xs font-semibold" style={{ color: "#8a5a1a" }}>Page {(a.file_idx || 0) + 1}{(a.page && a.page > 1) ? `·pg${a.page}` : ""}:</span> {a.text}
                                    {a.has_new_reply && <span data-testid={`annotation-new-reply-${i}`} className="ml-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: "var(--sj-primary)" }}>New reply</span>}
                                  </div>
                                  {(a.replies || []).length > 0 && (
                                    <div className="mt-1.5 space-y-1 pl-2 border-l-2" style={{ borderColor: "#F0E4CE" }}>
                                      {a.replies.map((r, ri) => (
                                        <div key={r.id || ri} data-testid={`annotation-reply-${i}-${ri}`} className="text-[13px]" style={{ color: "var(--sj-text-2)" }}>
                                          <b style={{ color: r.by_role === "tutor" ? "var(--sj-primary)" : "var(--sj-text)" }}>{r.by}{r.by_role === "tutor" ? " (Tutor)" : ""}:</b> {r.text}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="mt-1.5 flex items-center gap-1.5">
                                    <input data-testid={`annotation-reply-input-${i}`} value={annReply[a.id] || ""} onChange={(e) => setAnnReply((d) => ({ ...d, [a.id]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") sendAnnReply(mySub.id, a.id); }} placeholder="Reply to your tutor…" className="flex-1 text-[13px] border rounded-full px-3 py-1 outline-none" style={{ borderColor: "var(--sj-border)" }} />
                                    <button data-testid={`annotation-reply-send-${i}`} onClick={() => sendAnnReply(mySub.id, a.id)} disabled={annReplyBusy === a.id || !(annReply[a.id] || "").trim()} className="p-1.5 rounded-full text-white disabled:opacity-40" style={{ background: "var(--sj-primary)" }}><Send className="h-3.5 w-3.5" /></button>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      {(mySub.feedback_history || []).length > 0 && (
                        <div className="mt-2 space-y-1.5" data-testid="feedback-history">
                          <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Earlier feedback</div>
                          {mySub.feedback_history.map((h, i) => (
                            <div key={i} className="rounded-lg p-2 text-xs" style={{ background: "#F2F6F8", color: "var(--sj-text-2)" }}>
                              <b>{h.reviewed_by || "Tutor"}:</b> {h.feedback}
                            </div>
                          ))}
                        </div>
                      )}
                      {(() => {
                        const locked = mySub.status === "reviewed" || !!(mySub.feedback && mySub.feedback.trim()) || !!(mySub.mark && String(mySub.mark).trim());
                        if (locked && !mySub.resubmission_allowed) {
                          return <div data-testid="resubmit-locked-note" className="mt-3 rounded-lg px-3 py-2 text-xs" style={{ background: "#F2F6F8", color: "var(--sj-text-2)" }}>This assignment has been marked. Ask your tutor or class rep to approve a resubmission if you need to submit again.</div>;
                        }
                        return (
                          <div className="mt-3">
                            {locked && mySub.resubmission_allowed && mySub.resubmit_reason && (
                              <div data-testid="resubmit-reason-note" className="mb-2 rounded-lg px-3 py-2 text-xs" style={{ background: "#FFF7EC", color: "#8a5a1a" }}><b>From your tutor:</b> {mySub.resubmit_reason}</div>
                            )}
                            {!course.finished && <button data-testid="resubmit-assignment-toggle" onClick={() => { const c = { ...subs }; delete c[activeLesson.id]; setCourse({ ...course, my_submissions: c }); }}
                              className="text-xs font-medium underline" style={{ color: "var(--sj-primary)" }}>
                              {locked && mySub.resubmission_allowed ? "Resubmission approved — replace your submission" : "Replace submission"}
                            </button>}
                          </div>
                        );
                      })()}
                    </div>
                  ) : course.finished ? (
                    <div data-testid="assignment-closed-note" className="mt-3 rounded-lg px-3 py-2 text-xs" style={{ background: "#F2F6F8", color: "var(--sj-text-2)" }}>This course has finished, so submissions are closed.</div>
                  ) : (
                    <div className="mt-3 space-y-3">
                      <textarea data-testid="assignment-text-input" rows={3} placeholder="Type your response (optional if attaching a file)" value={subText} onChange={(e) => setSubText(e.target.value)}
                        className="w-full bg-white border rounded-xl outline-none px-3 py-2 text-sm" style={{ borderColor: "var(--sj-border)" }} />
                      <input ref={fileRef} data-testid="assignment-file-input" type="file" multiple onChange={(e) => setSubFiles((prev) => [...prev, ...Array.from(e.target.files || [])])} className="hidden" />
                      {subFiles.length > 0 && (
                        <div className="space-y-1.5" data-testid="attached-files-list">
                          {subFiles.length > 1 && <div className="text-[11px]" style={{ color: "var(--sj-text-3)" }}>Drag to reorder your pages before submitting.</div>}
                          {subFiles.map((f, i) => (
                            <div key={i} draggable
                              onDragStart={() => setDragFrom(i)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => { reorderFiles(dragFrom, i); setDragFrom(null); }}
                              onDragEnd={() => setDragFrom(null)}
                              data-testid={`attached-file-${i}`}
                              className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm cursor-grab active:cursor-grabbing" style={{ borderColor: dragFrom === i ? "var(--sj-primary)" : "var(--sj-border)", background: "#fff" }}>
                              <GripVertical className="h-4 w-4 shrink-0" style={{ color: "var(--sj-text-3)" }} />
                              <span className="text-xs font-bold w-5 text-center shrink-0" style={{ color: "var(--sj-text-3)" }}>{i + 1}</span>
                              {subPreviews[i]
                                ? <img src={subPreviews[i]} alt={f.name} data-testid={`attached-thumb-${i}`} className="h-10 w-10 rounded object-cover border shrink-0" style={{ borderColor: "var(--sj-border)" }} />
                                : <span className="h-10 w-10 rounded flex items-center justify-center border shrink-0" style={{ borderColor: "var(--sj-border)", background: "#F2F6F5" }}><FileText className="h-4 w-4" style={{ color: "var(--sj-primary)" }} /></span>}
                              <span className="flex-1 truncate" style={{ color: "var(--sj-text-2)" }}>{f.name}</span>
                              <button type="button" data-testid={`remove-attached-${i}`} onClick={() => setSubFiles((prev) => prev.filter((_, j) => j !== i))} className="text-xs font-semibold shrink-0" style={{ color: "#B91C1C" }}>Remove</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                          <Upload className="h-4 w-4" /> {subFiles.length > 0 ? "Add another page/document" : "Attach files"}
                        </button>
                        <button data-testid="submit-assignment-button" onClick={() => submitAssignment(activeLesson)} disabled={submitting}
                          className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-white text-sm font-semibold hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
                          {submitting ? "Submitting..." : "Submit Assignment"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className={`${CARD} p-12 text-center flex flex-col items-center gap-3`} style={{ color: "var(--sj-text-3)" }}>
              <PlayCircle className="h-10 w-10" /> Lessons will appear here soon.
            </div>
          )}
        </div>
      </div>

      {sessions.length > 0 && (
        <div className={`${CARD} p-5`} data-testid="live-classes">
          <div className="flex items-center gap-2 mb-1"><Video className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /><h3 className="font-head text-xl font-semibold">Next Live Class</h3></div>
          <p className="text-sm mb-3" style={{ color: "var(--sj-text-3)" }}>Your next live session. Joining today's class marks you present automatically.</p>
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} data-testid={`live-session-${s.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3" style={{ borderColor: s.is_today ? "var(--sj-primary)" : "var(--sj-border)", background: s.is_today ? "#F1F8F5" : "#fff" }}>
                <div>
                  <div className="text-sm font-semibold">{s.topic || "Class session"}{s.is_today && <span className="ml-2 text-[10px] uppercase font-bold rounded px-1.5 py-0.5" style={{ background: "var(--sj-primary)", color: "#fff" }}>Today</span>}</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{ukDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}{s.my_status ? ` · You: ${s.my_status}` : ""}</div>
                </div>
                <button data-testid={`join-session-${s.id}`} onClick={() => joinSession(s)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}><Video className="h-4 w-4" /> Join live class</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {(course.lessons?.some((l) => (l.assignment || "").trim()) || Object.keys(course.my_marks || {}).length > 0 || Object.keys(course.my_submissions || {}).length > 0) && (
        <div className={`${CARD} p-5`} data-testid="grade-summary">
          <div className="flex items-center gap-2 mb-1"><Award className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /><h3 className="font-head text-xl font-semibold">My Grades &amp; Feedback</h3></div>
          <p className="text-sm mb-3" style={{ color: "var(--sj-text-3)" }}>Your mark and tutor feedback for each assignment, all in one place.</p>
          <div className="space-y-2">
            {course.lessons.filter((l) => (l.assignment || "").trim() || course.my_submissions?.[l.id] || course.my_marks?.[l.id]).map((l) => {
              const sub = course.my_submissions?.[l.id];
              const mark = sub?.mark || course.my_marks?.[l.id] || "";
              const reviewed = sub?.status === "reviewed";
              const status = sub ? (reviewed ? "Reviewed" : "Submitted") : "Not submitted";
              return (
                <div key={l.id} data-testid={`grade-${l.id}`} className="rounded-xl border p-3" style={{ borderColor: "var(--sj-border)" }}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium text-sm">{l.title}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: reviewed ? "#E3F1EC" : sub ? "#FBEAD7" : "#EEF0F2", color: reviewed ? "var(--sj-primary)" : sub ? "#8a5a1a" : "var(--sj-text-3)" }}>{status}</span>
                      {mark && <span data-testid={`grade-mark-${l.id}`} className="text-sm font-bold px-2.5 py-0.5 rounded-full" style={{ background: "var(--sj-primary)", color: "#fff" }}>{mark}</span>}
                    </div>
                  </div>
                  {sub?.feedback && <p className="text-sm mt-1.5 rounded-lg p-2 whitespace-pre-wrap" style={{ background: "#F6FAF9", color: "var(--sj-text-2)" }}><b>Tutor feedback:</b> {sub.feedback}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      </div>)}

      {activeTab === "attendance" && course.is_class_rep && <RepConsole courseId={id} section="attendance" />}
      {activeTab === "submissions" && course.is_class_rep && <RepConsole courseId={id} section="submissions" />}

      {activeTab === "cohort" && cohortInfo && (
        <div className={`${CARD} p-6`} data-testid="my-cohort">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "var(--sj-text-3)" }}><GraduationCap className="h-4 w-4" /> My Cohort</div>
          <h3 className="font-head text-xl font-semibold">{cohortInfo.cohort_name || "No cohort"}</h3>

          {cohortInfo.tutors?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: "var(--sj-text-3)" }}>Tutor / Coordinator</div>
              {cohortInfo.tutors.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm py-0.5"><ShieldCheck className="h-4 w-4" style={{ color: "var(--sj-primary)" }} /> {t}</div>
              ))}
            </div>
          )}
          {cohortInfo.reps?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: "var(--sj-text-3)" }}>Class Rep{cohortInfo.reps.length > 1 ? "s" : ""}</div>
              {cohortInfo.reps.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-sm py-0.5"><UserPlus className="h-4 w-4" style={{ color: "var(--sj-secondary)" }} /> {r}</div>
              ))}
            </div>
          )}
          <div className="mt-4">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: "var(--sj-text-3)" }}>Classmates ({cohortInfo.learners?.length || 0})</div>
            <div className="space-y-0.5" data-testid="cohort-roster">
              {(cohortInfo.learners || []).map((l) => (
                <div key={l.member_id} className="flex items-center gap-2 text-sm py-0.5">
                  <Users className="h-4 w-4 shrink-0" style={{ color: "var(--sj-text-3)" }} />
                  <span>{l.name}{l.member_id === cohortInfo.my_member_id ? " (you)" : ""}</span>
                  {(cohortInfo.rep_ids || []).includes(l.member_id) && <span className="text-[10px] uppercase font-bold rounded px-1.5 py-0.5" style={{ background: "#E3F1EC", color: "var(--sj-primary)" }}>Rep</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "discussion" && cohortInfo && (
        <div className={`${CARD} p-6 flex flex-col`} data-testid="class-discussion">
          <div className="flex items-center gap-2 mb-1"><MessagesSquare className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /><h3 className="font-head text-xl font-semibold">Class Discussion</h3></div>
          <p className="text-sm mb-3" style={{ color: "var(--sj-text-3)" }}>Chat with your cohort, your class rep and your tutor.</p>
          <div className="flex-1 space-y-2 mb-3 max-h-[460px] overflow-y-auto" data-testid="discussion-posts">
            {posts.length === 0 ? (
              <div className="py-10 text-center text-sm" style={{ color: "var(--sj-text-3)" }}>No messages yet — say hello to your cohort! 👋</div>
            ) : posts.map((p) => (
              <div key={p.id} data-testid={`portal-post-${p.id}`} onClick={() => markSeen(p)} className={`rounded-xl border p-3 ${p.pinned ? "cursor-pointer" : ""}`} style={{ borderColor: p.pinned ? "var(--sj-primary)" : "var(--sj-border)", background: p.pinned ? "#F1F8F5" : (p.author_member_id === cohortInfo.my_member_id ? "#F6FAF9" : "#fff") }}>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  {p.pinned && <Pin className="h-3.5 w-3.5" style={{ color: "var(--sj-primary)" }} />}
                  {p.author_name}
                  {p.author_role === "tutor" && <span className="text-[10px] uppercase font-bold rounded px-1.5 py-0.5" style={{ background: "#EDF0FF", color: "#3B5BDB" }}>Tutor</span>}
                  {p.author_role === "rep" && <span className="text-[10px] uppercase font-bold rounded px-1.5 py-0.5" style={{ background: "#E3F1EC", color: "var(--sj-primary)" }}>Class Rep</span>}
                </div>
                <p className="text-sm mt-1 whitespace-pre-wrap" style={{ color: "var(--sj-text-2)" }}>{p.text}</p>
                <div className="text-[11px] mt-1" style={{ color: "var(--sj-text-3)" }}>{p.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <textarea data-testid="discussion-input" rows={2} value={discMsg} onChange={(e) => setDiscMsg(e.target.value)} placeholder="Write a message to your cohort…" className="flex-1 bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none px-4 py-2.5 text-sm" />
            <button data-testid="discussion-send-btn" disabled={discSending || !discMsg.trim()} onClick={sendDisc} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white text-sm font-semibold hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}><Send className="h-4 w-4" /> Send</button>
          </div>
        </div>
      )}


      <Dialog open={repOpen} onOpenChange={setRepOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">Add a Learner</DialogTitle><DialogDescription>Register a classmate onto this course.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-1">
            <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>Register a classmate onto {course.title}. They'll appear on the class register.</p>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="rep-learner-first" placeholder="First name*" value={repForm.first_name} onChange={(e) => setRepForm({ ...repForm, first_name: e.target.value })} className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none px-4 py-2.5 text-sm" />
              <input data-testid="rep-learner-last" placeholder="Last name" value={repForm.last_name} onChange={(e) => setRepForm({ ...repForm, last_name: e.target.value })} className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none px-4 py-2.5 text-sm" />
            </div>
            <input data-testid="rep-learner-email" placeholder="Email (optional)" value={repForm.email} onChange={(e) => setRepForm({ ...repForm, email: e.target.value })} className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none px-4 py-2.5 text-sm" />
            <input data-testid="rep-learner-phone" placeholder="Phone (optional)" value={repForm.phone} onChange={(e) => setRepForm({ ...repForm, phone: e.target.value })} className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none px-4 py-2.5 text-sm" />
            <DialogFooter>
              <button onClick={() => setRepOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button data-testid="rep-save-learner-btn" disabled={repSaving} onClick={addLearnerRep} className="rounded-full px-6 py-2.5 text-white font-semibold disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{repSaving ? "Adding…" : "Add learner"}</button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <DocViewer open={!!viewer} onClose={() => setViewer(null)} path={viewer?.path} fileName={viewer?.fileName} contentType={viewer?.contentType} subId={viewer?.subId} annotations={viewer?.annotations} fileIdx={viewer?.fileIdx || 0} canAnnotate={false} canReply={!!viewer?.subId} onAnnotationsChange={load} />
      <SubmissionCelebration open={!!celebrate} lessonTitle={celebrate} onClose={() => setCelebrate("")} />
    </div>
  );
}
